import { useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  CALENDAR_WRITABLE_SOURCES,
  formatTimeRange,
  minutesToInputTime,
  snapToQuarter,
  type CalendarWritableSource,
  type ISODate,
} from '@app/shared'

import { PALETTE } from './palette'
import { TIME_PRESETS } from '../tasks/presets'
import { useAnchoredPlacement, useDismissable, useEntered, type PeekAnchor } from '../../lib/popover'
import {
  ReminderPicker,
  fromReminderRows,
  remindersInvalid,
  toReminderRows,
  type ReminderRow,
} from './Reminders'

// ============================================================================
// The new-event composer, and the inline edit of a block that already exists — one form, because
// they ask for the same four facts and two forms would drift.
//
// WHAT IT NEVER DOES: appear over a Canvas block. It is opened by clicking empty grid or by
// clicking a block the owner owns, and a Canvas block is neither (brief rule 1 — the affordance
// is absent, not disabled).
//
// WHERE IT APPEARS — TWO PLACEMENTS, AND THE RULE IS WHERE THE GESTURE STARTED. A click on the
// grid (empty slot or existing block) carries a position, so the form opens BESIDE it: the owner
// is already looking at 3 PM on Thursday, and a form that opens at the top of the card makes him
// look somewhere else to type about the thing he just pointed at. The header's `+ New` button
// carries no position on the grid at all — it means "somewhere today" — so it keeps the centred
// placement it always had. That is the whole rule: anchored when the click named a slot, centred
// when it did not.
//
// THE UNDO CONTRACT. Every field here is an UNCONTROLLED-feeling controlled input with no
// autosave: nothing is written until Save is pressed, so the browser's own undo stack inside the
// title field is never interrupted by a re-render carrying a server value. This is the one thing
// on this screen no gate can check, and it is checked by hand: type, ⌘Z, the word comes back.
// ============================================================================

export interface ComposerDraft {
  /** Present when editing; absent when composing. */
  id?: string
  date: ISODate
  startMinutes: number
  endMinutes: number
  title: string
  source: CalendarWritableSource
  location: string | null
  taskId: string | null
  /**
   * Minutes before the start to be told about this block — `0` is "at start", `[]` is the normal
   * case. REPLACED on save, never merged, which is the Worker's own contract for the field.
   */
  reminders: number[]
}

const SOURCE_LABEL: Record<CalendarWritableSource, string> = {
  personal: 'Personal',
  work: 'Work',
  learning: 'Learning',
  task: 'Task',
}

/** The hue each source pill lights up with on hover — the palette's own rule colour. */
const SOURCE_HUE: Record<CalendarWritableSource, string> = {
  personal: 'var(--color-cal-personal-rule)',
  work: 'var(--color-cal-work-rule)',
  learning: 'var(--color-cal-learning-rule)',
  task: 'var(--color-cal-learning-rule)',
}

const WIDTH = 360

const clockToMinutes = (value: string): number =>
  Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5))

export function Composer({
  draft,
  anchor,
  onSave,
  onDelete,
  onCancel,
  pending,
  error,
}: {
  draft: ComposerDraft
  /**
   * The rect the form opens beside, or null for the centred placement. Null is a real answer,
   * not a missing one: it is what `+ New` passes, because that press names no slot.
   */
  anchor: PeekAnchor | null
  onSave: (next: ComposerDraft) => void
  onDelete?: (id: string) => void
  onCancel: () => void
  pending?: boolean
  error?: string
}) {
  const [title, setTitle] = useState(draft.title)
  const [source, setSource] = useState<CalendarWritableSource>(draft.source)
  const [start, setStart] = useState(minutesToInputTime(draft.startMinutes))
  const [end, setEnd] = useState(minutesToInputTime(draft.endMinutes))
  const [location, setLocation] = useState(draft.location ?? '')
  // Rows rather than raw minutes: two reminders can carry the same number, and a row that is
  // mid-edit in Custom… has no valid number yet. `Reminders.tsx` explains both.
  const [reminders, setReminders] = useState<ReminderRow[]>(() => toReminderRows(draft.reminders))
  const titleRef = useRef<HTMLInputElement | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => titleRef.current?.focus(), [])
  useDismissable(ref, onCancel)

  const startMinutes = snapToQuarter(clockToMinutes(start))
  const endMinutes = snapToQuarter(clockToMinutes(end))

  /**
   * A PRESET MOVES THE START AND CARRIES THE LENGTH WITH IT (owner, 2026-09-14: "when you add a
   * new block, it should give you some presets, like 11 am, 3 pm, and 10 pm, and then you should
   * be able to also select what specific time you want"). The block the composer opened with
   * already has a length — from the drag, or the hour the click landed in — and a chip that
   * silently reset it to sixty minutes would throw away the one thing the gesture said. A block
   * with no length yet (end at or before start) gets an hour, which is what a fresh block is.
   * The exact fields stay; the chip only fills them.
   */
  function presetStart(hhmm: string) {
    const from = clockToMinutes(hhmm)
    const length = endMinutes > startMinutes ? endMinutes - startMinutes : 60
    setStart(hhmm)
    setEnd(minutesToInputTime(Math.min(from + length, 23 * 60 + 45)))
  }
  // A block has a length. The form says so before it is sent rather than letting the API say it.
  const backwards = endMinutes <= startMinutes
  // A reminder the API would refuse is caught here instead of after the round trip.
  const badReminder = remindersInvalid(reminders)
  const blocked = title.trim().length === 0 || backwards || badReminder

  return (
    <ComposerShell innerRef={ref} anchor={anchor} draftId={draft.id}>
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-semibold leading-none text-ink">
          {draft.id ? 'Edit block' : 'New block'}
        </span>
        <span className="text-[10.5px] font-normal leading-none text-microcopy">
          {formatTimeRange(startMinutes, Math.max(endMinutes, startMinutes + 15))}
        </span>
      </div>

      <input
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What is this block?"
        className="mo-field rounded-button border border-line-2 px-[10px] py-[8px] text-[12px] leading-none text-ink outline-none placeholder:text-faint"
      />

      <div className="flex gap-[2px] rounded-pill border border-line bg-surface p-[3px]">
        {CALENDAR_WRITABLE_SOURCES.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={source === key}
            onClick={() => setSource(key)}
            // The ink arrives in the LANE'S OWN HUE, so hovering Work says "this block would be
            // orange" before the press commits it — §1's first job, verbatim.
            style={{ '--mo-rule': SOURCE_HUE[key] } as CSSProperties}
            className={[
              'mo-ink mo-press flex-1 rounded-[14px] px-[10px] py-[4px] text-[11px] leading-none',
              source === key
                ? `${PALETTE[key].tint} ${PALETTE[key].ink} font-semibold`
                : 'font-medium text-muted',
            ].join(' ')}
          >
            {SOURCE_LABEL[key]}
          </button>
        ))}
      </div>

      {/* THE START PRESETS. Same three clocks the Reminders composers offer (tasks/presets.ts),
          so the two screens agree on what "the usual times" are. `aria-pressed` reads off the
          start field, so a chip lights when the exact field is set to its clock by any route. */}
      <div className="flex items-center gap-[6px]" role="group" aria-label="Start presets">
        {TIME_PRESETS.map((p) => (
          <button
            key={p.hhmm}
            type="button"
            onClick={() => presetStart(p.hhmm)}
            aria-pressed={start === p.hhmm}
            style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
            className={[
              'mo-rule flex h-[24px] flex-none items-center rounded-pill border px-[9px] text-[11px] font-medium leading-none',
              start === p.hhmm
                ? 'border-ink bg-ink text-surface'
                : 'border-line-2 bg-surface text-ink-2 hover:bg-fill-2',
            ].join(' ')}
          >
            {p.label}
          </button>
        ))}
        <span className="ml-auto text-[10.5px] leading-none text-microcopy">or set it exactly</span>
      </div>

      {/* `<input type="time">` takes 24-hour 'HH:MM' in `value` and renders it in the reader's
          own locale — which is 12-hour here. This is the one place the 24-hour string survives,
          and it never reaches a `<span>`: the readout above is `formatTimeRange`. */}
      <div className="flex items-center gap-2">
        <input
          type="time"
          step={900}
          value={start}
          onChange={(e) => setStart(e.target.value)}
          aria-label="Starts at"
          className="mo-field flex-1 rounded-button border border-line-2 px-[10px] py-[7px] text-[12px] leading-none text-ink"
        />
        <span className="text-[11px] text-microcopy">–</span>
        <input
          type="time"
          step={900}
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          aria-label="Ends at"
          className="mo-field flex-1 rounded-button border border-line-2 px-[10px] py-[7px] text-[12px] leading-none text-ink"
        />
      </div>

      <input
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        placeholder="Where (optional)"
        className="mo-field rounded-button border border-line-2 px-[10px] py-[8px] text-[12px] leading-none text-ink outline-none placeholder:text-faint"
      />

      <ReminderPicker rows={reminders} onChange={setReminders} />

      {backwards && (
        <div className="text-[10.5px] leading-[1.4] text-danger-2">
          The end is before the start, so there is no block to save. Move one of them.
        </div>
      )}
      {badReminder && (
        <div className="text-[10.5px] leading-[1.4] text-danger-2">
          A reminder needs a whole number of minutes between 0 and 40320 — four weeks. Fix it or
          remove the row.
        </div>
      )}
      {error && <div className="text-[10.5px] leading-[1.4] text-danger-2">{error}</div>}

      <div className="flex items-center gap-2">
        {draft.id && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(draft.id!)}
            style={{ '--mo-rule': 'var(--color-danger-2)' } as CSSProperties}
            className="mo-ink mo-press rounded-pill px-[11px] py-[6px] text-[11px] font-semibold leading-none text-danger-2"
          >
            Delete
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
          className="mo-ink mo-press ml-auto rounded-pill px-[11px] py-[6px] text-[11px] font-medium leading-none text-muted"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={blocked || pending}
          onClick={() =>
            onSave({
              ...draft,
              title: title.trim(),
              source,
              startMinutes,
              endMinutes,
              location: location.trim() || null,
              reminders: fromReminderRows(reminders),
            })
          }
          // A LIGHT INK ON A DARK GROUND. Save is `bg-ink`, so the 10% mix of `--color-ink` the
          // other pills use would be invisible on it — the wash has to come from the surface hue
          // for the fill to read at all. Disabled is excluded by `.mo-ink`'s own rules upstream.
          style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
          className="mo-ink mo-press rounded-pill bg-ink px-[13px] py-[7px] text-[11px] font-semibold leading-none text-surface disabled:opacity-40"
        >
          {pending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </ComposerShell>
  )
}

/**
 * The panel itself, in whichever of its two placements applies.
 *
 * Split out because the anchored branch runs a layout effect and the centred one must not — a
 * hook cannot be called conditionally, and the alternative (one component measuring a box it is
 * not going to use) is the kind of thing that is correct today and wrong after the next edit.
 */
function ComposerShell({
  innerRef,
  anchor,
  draftId,
  children,
}: {
  innerRef: React.RefObject<HTMLDivElement | null>
  anchor: PeekAnchor | null
  draftId: string | undefined
  children: React.ReactNode
}) {
  // THE SAME ENTRANCE THE PEEK USES, because they are the same object with different contents —
  // 180ms on `--ease-ink`, opacity plus a 4px settle, which is the motion system's panel beat
  // (index.css → §2 and 13a's `mo-palette`). It is a TRANSITION rather than an animation on
  // purpose: the anchored branch writes `left`/`top` in a layout effect, and a keyframed
  // transform is the one thing that could fight a measured position for the first frame.
  //
  // Reduced motion needs nothing here: the global block collapses transitions to ~0ms, so the
  // panel still arrives at its end state, it just does not travel (§2, non-negotiable). Nothing
  // below sets a duration inline, which is what keeps that true.
  const entered = useEntered()
  const shell = [
    'flex w-[360px] flex-col gap-[11px] rounded-panel border border-line-2 bg-surface p-4 shadow-lifted',
    'transition-[opacity,transform] duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
    entered ? 'opacity-100' : 'opacity-0',
  ].join(' ')
  const enterStyle: CSSProperties = { transform: entered ? 'translateY(0)' : 'translateY(-4px)' }
  const label = draftId ? 'Edit block' : 'New block'

  if (!anchor) {
    return (
      <div
        ref={innerRef}
        role="dialog"
        aria-label={label}
        className={`absolute inset-x-0 top-4 z-10 mx-auto ${shell}`}
        style={enterStyle}
      >
        {children}
      </div>
    )
  }
  return (
    <AnchoredComposer
      innerRef={innerRef}
      anchor={anchor}
      className={shell}
      label={label}
      enterStyle={enterStyle}
    >
      {children}
    </AnchoredComposer>
  )
}

function AnchoredComposer({
  innerRef,
  anchor,
  className,
  label,
  enterStyle,
  children,
}: {
  innerRef: React.RefObject<HTMLDivElement | null>
  anchor: PeekAnchor
  className: string
  label: string
  /** The entrance's transform. It rides ALONGSIDE the measured position, never instead of it. */
  enterStyle: CSSProperties
  children: React.ReactNode
}) {
  const placement = useAnchoredPlacement(innerRef, anchor, WIDTH)
  return (
    <div
      ref={innerRef}
      role="dialog"
      aria-label={label}
      className={`fixed z-20 ${className}`}
      // `left`/`top` are the measured, zoom-corrected position (popover.ts); the transform is a
      // 4px nudge on top of it. A percentage here would fight the placement instead of riding it.
      style={{ ...enterStyle, left: placement.left, top: placement.top }}
    >
      {children}
    </div>
  )
}
